import {IVanillaReaderUI} from "./IVanillaReaderUI";
import {VanillaReaderUiMenuMediaPlayerController} from "./VanillaReaderUiMenuMediaPlayerController";
import {IVanillaSyncMediaPlaybackStateData} from "../VanillaReader/VanillaReaderModel";
import {
    IPublicationMetadataItem
} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";

export interface VanillaReaderUiMediaSessionsController extends IVanillaReaderUI, VanillaReaderUiMenuMediaPlayerController {}

/**
 * # VanillaReaderUiMediaSessionsController
 *
 * ## RESPONSIBILITIES
 * This controller mixing class handles media playback related events from the `VanillaReader` class.
 *
 * Just as all other controller mixin classes, this class is used to add functionality to the `VanillaReaderUi` class
 * in a "modular" way. Read more about this in the `UI Components and Controller Mixins` in the README.md file.
 *
 * ## RELATED TYPES
 * - VanillaReaderUi
 * - VanillaReaderUiMenuMediaPlayerController
 * - MediaSession (https://developer.mozilla.org/en-US/docs/Web/API/MediaSession)
 *
 */
export class VanillaReaderUiMediaSessionsController {

    /*
    *
    * Media Session event setup to support background media playback actions from the lockscreen etc.
    * Works for Audiobooks and EPUBs that have Media Overlays.
    *
    * Check out the `_vanillaReaderEvent_mediaPlayerPlaybackStateChanged`, `_vanillaReaderEvent_mediaPlayerPlaybackPositionChanged` and
    * `_vanillaReaderEvent_mediaPlayerSeekCompleted` for more mediaSession related stuff.
    *
    * */

    _setUpMediaSessionActionHandlers(): void {

        if (window.navigator.mediaSession) {
            window.navigator.mediaSession.setActionHandler('nexttrack', (_actionDetails) => {
                this.seekNext();
            });

            window.navigator.mediaSession.setActionHandler('previoustrack', (_actionDetails) => {
                this.seekPrevious();
            });

            window.navigator.mediaSession.setActionHandler('pause', (_actionDetails) => {
                this.pausePlayback();
            });

            window.navigator.mediaSession.setActionHandler('play', (_actionDetails) => {
                this.startPlayback();
            });

            window.navigator.mediaSession.setActionHandler('seekto', (actionDetails) => {
                if (actionDetails.seekTime) {
                    let segmentStartOffset = this.latestSyncMediaPlaybackStateData?.segmentData?.segmentTimelineStartOffsetMs;
                    if(segmentStartOffset !== undefined) {
                        let seekTimeInSeconds = segmentStartOffset + actionDetails.seekTime * 1000;
                        this.navigateToTimelinePosition(seekTimeInSeconds);
                    }
                }
            });

            window.navigator.mediaSession.setActionHandler('seekforward', (_actionDetails) => {
                this.seekForward();
            });

            window.navigator.mediaSession.setActionHandler('seekbackward', (_actionDetails) => {
                this.seekBackward();
            });
        }
    }

    onMediaPlayerPlaybackPositionChanged(state: IVanillaSyncMediaPlaybackStateData): void {
        if(state.segmentData?.approximateDurationMs !== undefined && state.positionWithinSegment !== undefined) {
            let positionInSeconds = state.positionWithinSegment / 1000;
            let durationInSeconds = state.segmentData?.approximateDurationMs / 1000;
            window.navigator.mediaSession.setPositionState({
                position: positionInSeconds,
                duration: durationInSeconds,
                playbackRate: state.rate,
            });
        }

    }

    onMediaPlayerPlaybackStateChanged(state: IVanillaSyncMediaPlaybackStateData): void {

        if (window.navigator.mediaSession) {
            let isPaused: boolean = state.isPaused || false;

            if (isPaused) {
                window.navigator.mediaSession.playbackState = 'paused';
            } else {
                window.navigator.mediaSession.playbackState = 'playing';
            }

        }
    }

    onMediaPlayerSeekCompleted(state: IVanillaSyncMediaPlaybackStateData): void {
        let segmentTitle = state.navigationItems ? state.navigationItems[0].title : 'Untitled';
        let artistMetadata: IPublicationMetadataItem | undefined = this.initialPublicationData?.metadata?.all.find((item)=>{
            return item.property.name === 'author';
        });

        window.navigator.mediaSession.metadata = new MediaMetadata({
            title: segmentTitle,
            album: this.initialPublicationData?.title,
            artwork: [{
                src: `data:;base64,${this.initialPublicationData?.coverImageAsBase64}`
            }],
            artist: artistMetadata?.content.value ||'Unknown'
        });

    }
}
